<template>
  <div class="order-form">
    <div class="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 sm:gap-0 mb-4 sm:mb-6"
      v-if="!embedded">
      <h1 class="text-xl sm:text-2xl font-bold text-surface-900 dark:text-surface-100 tracking-tight">
        {{ isEditing ? 'Edit Order' : 'Create New Order' }}
      </h1>
      <button @click="router.back()"
        class="w-full sm:w-auto px-4 py-2 text-surface-700 dark:text-surface-300 border border-surface-300 dark:border-surface-600 rounded-lg hover:bg-surface-50 dark:hover:bg-surface-800 touch-action-manipulation transition-colors">
        Cancel
      </button>
    </div>

    <LoadingSpinner v-if="initialLoading" text="Loading form data..." />

    <!-- Edit Permission Warning -->
    <div v-if="isEditing && !editPermission.allowed"
      class="mb-6 p-4 bg-error-50 dark:bg-error-900/20 border border-error-200 dark:border-error-800 rounded-lg">
      <div class="flex items-center">
        <i class="fas fa-exclamation-triangle text-error-600 dark:text-error-400 mr-3"></i>
        <div>
          <h3 class="text-sm font-medium text-error-800 dark:text-error-200">Editing Restricted</h3>
          <p class="text-sm text-error-700 dark:text-error-300 mt-1">{{ editPermission.reason }}</p>
        </div>
      </div>
    </div>

    <!-- Super Admin Warning for Restricted Orders -->
    <div v-if="isEditing && isSuperAdmin && isEditingRestrictedOrder(currentOrder)"
      class="mb-6 p-4 bg-warning-50 dark:bg-warning-900/20 border border-warning-200 dark:border-warning-800 rounded-lg">
      <div class="flex items-center">
        <i class="fas fa-exclamation-triangle text-warning-600 dark:text-warning-400 mr-3"></i>
        <div>
          <h3 class="text-sm font-medium text-warning-800 dark:text-warning-200">Super Admin Override</h3>
          <p class="text-sm text-warning-700 dark:text-warning-300 mt-1">
            You are editing an order in <strong>{{ currentOrder?.status?.replace('_', ' ').toUpperCase() }}</strong>
            status.
            This may affect the production workflow. Please ensure this change is necessary.
          </p>
        </div>
      </div>
    </div>

    <form v-if="!isEditing || editPermission.allowed || isSuperAdmin" @submit.prevent="handleSubmit"
      :class="'flex flex-col lg:flex-row gap-4 lg:gap-6'">
      <!-- Left Column - Main Form (3/4 width) -->
      <div :class="'w-full lg:flex-1'" class="bg-white dark:bg-dark-800 rounded-lg shadow-sm border border-gray-100 dark:border-dark-700 p-4 sm:p-6">
        <!-- Client Selection -->
        <div class="mb-6 relative client-search">
          <label class="flex items-center gap-2 text-sm font-medium text-surface-700 dark:text-surface-300 mb-2">
            <span>Client</span>
            <span v-if="!formData.client_id" class="text-xs font-normal text-surface-500 dark:text-surface-400">(Required)</span>
            <i v-else class="fas fa-check-circle text-success-600 dark:text-success-400 text-xs"></i>
          </label>
          <div class="relative">
            <input type="text" v-model="clientSearch" @input="!isEditing && debouncedSearchClients"
              @focus="!isEditing && (showClientResults = true)" @blur="handleClientSearchBlur"
              :placeholder="isEditing ? '' : 'Search client by name...'"
              class="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 bg-surface-50 dark:bg-surface-700 border-surface-300 dark:border-surface-600 text-surface-900 dark:text-surface-100 placeholder-surface-500 dark:placeholder-surface-400"
              :class="{ 'border-error-500': validationErrors.client_id || clientSearchError, 'bg-surface-100 dark:bg-surface-600 cursor-not-allowed': isEditing || (isEditing && !editPermission.allowed) }"
              :disabled="isEditing || (isEditing && !editPermission.allowed)" required />
            <!-- Client Search Results -->
            <div v-if="!isEditing && showClientResults && !clientSearchLoading && filteredClients.length > 0"
              class="absolute z-50 w-full mt-1 bg-surface-0 dark:bg-surface-800 border border-surface-300 dark:border-surface-600 rounded-lg shadow-lg max-h-[50vh] sm:max-h-60 overflow-y-auto">
              <div v-for="client in filteredClients" :key="client.id" @mousedown="selectClient(client)"
                class="px-4 py-2 hover:bg-surface-50 dark:hover:bg-surface-700 cursor-pointer transition-colors">
                <div class="font-medium flex items-center justify-between text-surface-900 dark:text-surface-100">
                  <span>{{ getClientDisplayName(client) }}</span>
                  <div class="flex items-center gap-2">
                    <span v-if="client.classification" class="px-2 py-0.5 text-xs rounded-full"
                      :class="getClassificationClass(client.classification)">
                      {{ client.classification.toUpperCase() }}
                    </span>
                    <span v-if="client.is_active === false"
                      class="px-2 py-0.5 text-xs rounded-full bg-error-100 dark:bg-error-900/30 text-error-800 dark:text-error-300">
                      INACTIVE
                    </span>
                  </div>
                </div>
                <div class="text-sm text-surface-500 dark:text-surface-400">
                  <!-- Show first_name and last_name if company_name is already displayed as the main name -->
                  <template
                    v-if="client.company_name && client.company_name === getClientDisplayName(client) && (client.first_name || client.last_name)">
                    {{ [client.first_name, client.last_name].filter(Boolean).join(' ') }}
                  </template>
                  <!-- Show company_name if it's not already displayed as the main name -->
                  <template v-else-if="client.company_name && client.company_name !== getClientDisplayName(client)">
                    {{ client.company_name }}
                  </template>
                  <div v-if="client.email">{{ client.email }}</div>
                  <div v-if="client.phone">{{ client.phone }}</div>
                </div>
              </div>
            </div>
            <!-- Loading State -->
            <div v-if="!isEditing && clientSearchLoading"
              class="absolute z-10 w-full mt-1 bg-surface-0 dark:bg-surface-800 border border-surface-300 dark:border-surface-600 rounded-lg shadow-lg p-4 text-center">
              <LoadingSpinner text="Searching clients..." />
            </div>
            <!-- No Results -->
            <div
              v-if="!isEditing && showClientResults && !clientSearchLoading && filteredClients.length === 0 && clientSearch"
              class="absolute z-10 w-full mt-1 bg-surface-0 dark:bg-surface-800 border border-surface-300 dark:border-surface-600 rounded-lg shadow-lg p-4 text-center text-surface-500 dark:text-surface-400">
              No clients found
            </div>
            <!-- Error Message -->
            <div v-if="validationErrors.client_id" class="mt-1 text-sm text-error-600 dark:text-error-400">
              {{ validationErrors.client_id }}
            </div>
            <!-- Selected Client Display -->
            <div v-if="selectedClient"
              class="mt-2 p-3 bg-surface-50 dark:bg-surface-700 rounded-lg border border-surface-200 dark:border-surface-600">
              <div class="font-medium flex items-center justify-between text-surface-900 dark:text-surface-100">
                <span>{{ getClientDisplayName(selectedClient) }}</span>
                <div class="flex items-center gap-2">
                  <span v-if="selectedClient.classification" class="px-2 py-0.5 text-xs rounded-full"
                    :class="getClassificationClass(selectedClient.classification)">
                    {{ selectedClient.classification.toUpperCase() }}
                  </span>
                  <span v-if="selectedClient.is_active === false"
                    class="px-2 py-0.5 text-xs rounded-full bg-error-100 dark:bg-error-900/30 text-error-800 dark:text-error-300">
                    INACTIVE
                  </span>
                </div>
              </div>
              <!-- Show first_name and last_name if company_name is already displayed as the main name -->
              <div
                v-if="selectedClient.company_name && selectedClient.company_name === getClientDisplayName(selectedClient) && (selectedClient.first_name || selectedClient.last_name)"
                class="text-sm text-surface-500 dark:text-surface-400">
                {{ [selectedClient.first_name, selectedClient.last_name].filter(Boolean).join(' ') }}
              </div>
              <!-- Show company_name if it's not already displayed as the main name -->
              <div
                v-else-if="selectedClient.company_name && selectedClient.company_name !== getClientDisplayName(selectedClient)"
                class="text-sm text-surface-500 dark:text-surface-400">
                {{ selectedClient.company_name }}
              </div>
              <div v-if="selectedClient.email" class="text-sm text-surface-500 dark:text-surface-400">{{
                selectedClient.email }}
              </div>
              <div v-if="selectedClient.phone" class="text-sm text-surface-500 dark:text-surface-400">{{
                selectedClient.phone }}
              </div>
            </div>

            <!-- Expected Tax Info (Internal Only) -->
            <div v-if="selectedClient && selectedClient.withholding_tax_rate > 0 && finalAmount > 0"
              class="mt-2 p-3 bg-info-50 dark:bg-info-900/20 rounded-lg border border-info-200 dark:border-info-700">
              <div class="text-xs font-medium text-info-900 dark:text-info-200 mb-1">
                <i class="fas fa-info-circle mr-1"></i>
                Expected Payment Info (Internal)
              </div>
              <div class="text-xs space-y-1 text-info-800 dark:text-info-300">
                <div>Expected Withholding Tax ({{ selectedClient.withholding_tax_rate }}%): ₱{{
                  formatPrice(expectedTaxAmount) }}
                </div>
                <div>Expected Net Payment: ₱{{ formatPrice(expectedNetPayment) }}</div>
              </div>
            </div>
          </div>
        </div>

        <!-- Department and Order Type -->
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6 mb-4 lg:mb-6">
          <DepartmentSelector v-model="formData.department_id" :required="true" :error="validationErrors.department_id"
            @change="(value) => formData.department_id = value" />

          <OrderTypeSelector v-model="formData.order_type_id" :required="true" :error="validationErrors.order_type_id"
            :client-id="formData.client_id" :order-amount="totalAmount" :available-types="filteredOrderTypes"
            @validation="handleOrderTypeValidation" @change="(value) => formData.order_type_id = value" />
        </div>

        <!-- Priority, Assigned To, Gateway -->
        <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6 mb-4 lg:mb-6">
          <!-- Priority -->
          <div>
            <label class="block text-sm font-medium text-surface-700 dark:text-surface-300 mb-2">Priority</label>
            <select v-model="formData.priority"
              class="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 bg-surface-50 dark:bg-surface-700 border-surface-300 dark:border-surface-600 text-surface-900 dark:text-surface-100"
              required>
              <option value="normal">Normal</option>
              <option value="urgent">Urgent</option>
              <option value="high">High</option>
              <option value="low">Low</option>
            </select>
          </div>

          <!-- Assigned To -->
          <div>
            <label class="flex items-center gap-2 text-sm font-medium text-surface-700 dark:text-surface-300 mb-2">
              <span>Assigned To</span>
              <span v-if="!formData.assigned_to" class="text-xs font-normal text-surface-500 dark:text-surface-400">(Required)</span>
              <i v-else class="fas fa-check-circle text-success-600 dark:text-success-400 text-xs"></i>
            </label>
            <select v-model="formData.assigned_to"
              class="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 bg-surface-50 dark:bg-surface-700 border-surface-300 dark:border-surface-600 text-surface-900 dark:text-surface-100"
              :class="{ 'border-error-500': validationErrors.assigned_to }" required>
              <option value="">Select Staff</option>
              <option v-for="user in users" :key="user.id" :value="user.id">
                {{ user.first_name }} {{ user.last_name }}
              </option>
            </select>
            <div v-if="validationErrors.assigned_to" class="mt-1 text-sm text-error-600 dark:text-error-400">
              {{ validationErrors.assigned_to }}
            </div>
          </div>

          <!-- Gateway Selection -->
          <div>
            <label class="flex items-center gap-2 text-sm font-medium text-surface-700 dark:text-surface-300 mb-2">
              <span>Gateway</span>
              <span v-if="!formData.gateway_id" class="text-xs font-normal text-surface-500 dark:text-surface-400">(Required)</span>
              <i v-else class="fas fa-check-circle text-success-600 dark:text-success-400 text-xs"></i>
            </label>
            <select v-model="formData.gateway_id"
              class="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 bg-surface-50 dark:bg-surface-700 border-surface-300 dark:border-surface-600 text-surface-900 dark:text-surface-100"
              :class="{ 'border-error-500': validationErrors.gateway_id }" required>
              <option value="">Select Gateway</option>
              <option v-for="gateway in gateways" :key="gateway.id" :value="gateway.id">
                {{ gateway.name }}
              </option>
            </select>
            <div v-if="validationErrors.gateway_id" class="mt-1 text-sm text-error-600 dark:text-error-400">
              {{ validationErrors.gateway_id }}
            </div>
          </div>
        </div>
        <!-- Dates Section -->
        <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6 mb-4 lg:mb-6">
          <!-- Date Received -->
          <div>
            <label class="flex items-center gap-2 text-sm font-medium text-surface-700 dark:text-surface-300 mb-2">
              <span>Date Received</span>
              <span v-if="!formData.date_received" class="text-xs font-normal text-surface-500 dark:text-surface-400">(Required)</span>
              <i v-else class="fas fa-check-circle text-success-600 dark:text-success-400 text-xs"></i>
            </label>
            <input type="date" v-model="formData.date_received"
              class="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 bg-surface-50 dark:bg-surface-700 border-surface-300 dark:border-surface-600 text-surface-900 dark:text-surface-100"
              :class="{ 'border-error-500': validationErrors.date_received }" required />
            <div v-if="validationErrors.date_received" class="mt-1 text-sm text-error-600 dark:text-error-400">
              {{ validationErrors.date_received }}
            </div>
          </div>

          <!-- Completion Date -->
          <div>
            <label class="flex items-center gap-2 text-sm font-medium text-surface-700 dark:text-surface-300 mb-2">
              <span>Completion Date</span>
              <span v-if="!formData.completion_date" class="text-xs font-normal text-surface-500 dark:text-surface-400">(Required)</span>
              <i v-else class="fas fa-check-circle text-success-600 dark:text-success-400 text-xs"></i>
            </label>
            <input type="date" v-model="formData.completion_date"
              class="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 bg-surface-50 dark:bg-surface-700 border-surface-300 dark:border-surface-600 text-surface-900 dark:text-surface-100"
              :class="{ 'border-error-500': validationErrors.completion_date }" required />
            <div v-if="validationErrors.completion_date" class="mt-1 text-sm text-error-600 dark:text-error-400">
              {{ validationErrors.completion_date }}
            </div>
          </div>

          <!-- Deadline -->
          <div>
            <label class="flex items-center gap-2 text-sm font-medium text-surface-700 dark:text-surface-300 mb-2">
              <span>Deadline</span>
              <span v-if="!formData.deadline" class="text-xs font-normal text-surface-500 dark:text-surface-400">(Required)</span>
              <i v-else class="fas fa-check-circle text-success-600 dark:text-success-400 text-xs"></i>
            </label>
            <input type="date" v-model="formData.deadline"
              class="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 bg-surface-50 dark:bg-surface-700 border-surface-300 dark:border-surface-600 text-surface-900 dark:text-surface-100"
              :class="{ 'border-error-500': validationErrors.deadline }" required />
            <div v-if="validationErrors.deadline" class="mt-1 text-sm text-error-600 dark:text-error-400">
              {{ validationErrors.deadline }}
            </div>
          </div>
        </div>

        <!-- Notes and Image Upload in one row -->
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6">
          <!-- Notes -->
          <div>
            <label class="block text-sm font-medium text-surface-700 dark:text-surface-300 mb-2">Notes</label>
            <textarea v-model="formData.notes" rows="4"
              class="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 bg-surface-50 dark:bg-surface-700 border-surface-300 dark:border-surface-600 text-surface-900 dark:text-surface-100 placeholder-surface-500 dark:placeholder-surface-400"></textarea>
          </div>

          <!-- Order Image -->
          <div>
            <label class="block text-sm font-medium text-surface-700 dark:text-surface-300 mb-2">Order Image</label>
            <div class="flex items-center space-x-4">
              <!-- Image Upload Area -->
              <div class="flex-1 border-2 border-dashed rounded-lg p-3 text-center transition-colors cursor-pointer"
                :class="[
                  imagePreview || formData.image_url ?
                    'border-success-300 dark:border-success-600 bg-success-50 dark:bg-success-900/20' :
                    isDragging ? 'border-primary-400 dark:border-primary-400 bg-primary-50 dark:bg-primary-900/20' : 'border-surface-300 dark:border-surface-600'
                ]" @click="$refs.imageInput.click()" @dragover.prevent="isDragging = true"
                @dragleave.prevent="isDragging = false" @drop.prevent="handleImageDrop"
                @paste.prevent="handleImagePaste" tabindex="0" @keydown.prevent ref="pasteArea">
                <input ref="imageInput" type="file" class="hidden" accept="image/jpeg,image/png,image/gif"
                  @change="handleImageUpload" />
                <div class="space-y-2">
                  <i class="fas fa-image text-xl text-surface-400 dark:text-surface-400"></i>
                  <div class="text-sm text-surface-600 dark:text-surface-400 space-y-1">
                    <p class="font-medium text-xs">Order image upload:</p>
                    <div class="flex items-center justify-center gap-3 text-xs">
                      <span class="flex items-center gap-1">
                        <i class="fas fa-mouse-pointer text-primary-500"></i>
                        Browse
                      </span>
                      <span class="flex items-center gap-1 text-success-600 dark:text-success-400 font-semibold">
                        <i class="fas fa-paste text-success-500"></i>
                        Ctrl+V
                      </span>
                      <span class="flex items-center gap-1">
                        <i class="fas fa-hand-paper text-secondary-500"></i>
                        Drag
                      </span>
                    </div>
                    <div
                      class="bg-success-50 dark:bg-success-900/20 border border-success-200 dark:border-success-600 rounded p-1">
                      <p class="text-success-700 dark:text-success-300 text-xs text-center">
                        <i class="fas fa-lightbulb mr-1"></i>
                        Copy image & press Ctrl+V
                      </p>
                    </div>
                    <p class="text-xs text-surface-500 dark:text-surface-400 text-center">PNG, JPG, GIF (10MB max)</p>
                  </div>
                </div>
              </div>

              <!-- Preview Image -->
              <div v-if="imagePreview || formData.image_url" class="relative w-24 h-24">
                <img :src="imagePreview || formData.image_url" class="w-24 h-24 object-cover rounded-lg"
                  alt="Order image preview" />
                <button @click.stop="removeImage" type="button"
                  class="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600">
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
            <div v-if="validationErrors.image_url" class="mt-1 text-sm text-error-600 dark:text-error-400">
              {{ validationErrors.image_url }}
            </div>
          </div>
        </div>
      </div>

      <!-- Right Column - Order Items & Total (1/4 width) -->
      <div :class="'w-full lg:w-1/4 lg:min-w-[300px]'" class="bg-white dark:bg-dark-800 rounded-lg shadow-sm border border-gray-100 dark:border-dark-700">
        <!-- Order Items Header -->
        <div class="p-4 sm:p-6 border-b border-surface-200 dark:border-surface-600">
          <div class="flex justify-between items-center">
            <h3 class="text-base sm:text-lg font-medium text-surface-900 dark:text-surface-100">Order Items</h3>
            <button type="button" @click="addItem"
              class="px-3 py-1.5 text-sm text-primary-600 dark:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/30 rounded-lg touch-action-manipulation">
              <i class="fas fa-plus mr-1"></i>
              <span class="hidden sm:inline">Add Item</span>
              <span class="sm:hidden">Add</span>
            </button>
          </div>
        </div>

        <!-- Order Items List -->
        <div class="p-4 sm:p-6 space-y-4 max-h-[calc(100vh-400px)] sm:max-h-[calc(100vh-300px)] overflow-y-auto">
          <div v-if="validationErrors.items" class="mb-2 text-sm text-red-600 dark:text-red-400">
            {{ validationErrors.items }}
          </div>

          <div v-for="(item, index) in formData.items" :key="index"
            class="bg-gray-50 dark:bg-dark-700 rounded-lg border border-gray-200 dark:border-dark-600 overflow-hidden">
            <!-- Collapsible Header -->
            <div class="p-3 sm:p-4 cursor-pointer hover:bg-gray-100 dark:hover:bg-dark-600 transition-colors"
              @click="toggleItem(index)">
              <div class="flex items-center justify-between">
                <div class="flex-1 min-w-0">
                  <div class="flex items-center gap-2">
                    <i :class="[
                      'fas transition-transform duration-200 text-gray-500 dark:text-gray-400',
                      expandedItems.has(index) ? 'fa-chevron-down' : 'fa-chevron-right'
                    ]"></i>
                    <span class="font-medium text-sm text-gray-900 dark:text-dark-100">
                      {{ item.productSearch || `Item ${index + 1}` }}
                    </span>
                    <span v-if="item.product_id" class="text-xs text-gray-500 dark:text-gray-400">
                      • ₱{{ formatPrice(item.total || 0) }}
                    </span>
                  </div>
                </div>
                <button type="button" @click.stop="removeItem(index)"
                  class="ml-2 text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300 text-sm px-2 py-1 hover:bg-red-50 dark:hover:bg-red-900/20 rounded">
                  <i class="fas fa-trash"></i>
                </button>
              </div>
            </div>
            
            <!-- Collapsible Content -->
            <div v-show="expandedItems.has(index)" class="px-3 sm:px-4 pb-3 sm:pb-4 space-y-2 border-t border-gray-200 dark:border-dark-600 pt-3">
            <!-- Product Selection -->
            <div class="mb-2 relative">
              <label class="flex items-center gap-1.5 text-xs font-medium text-gray-700 dark:text-dark-300">
                <span>Product</span>
                <span v-if="!item.product_id" class="text-[10px] font-normal text-gray-500 dark:text-gray-400">(Required)</span>
                <i v-else class="fas fa-check-circle text-success-600 dark:text-success-400 text-[10px]"></i>
              </label>
              <div class="relative">
                <input type="text" v-model="item.productSearch" @input="() => debouncedSearchProducts(index)"
                  @focus="() => showProductResults[index] = true" @blur="handleProductSearchBlur(index)"
                  :placeholder="'Search for a product...'"
                  class="w-full px-2 py-1 text-sm border rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-dark-700 border-gray-300 dark:border-dark-600 text-gray-900 dark:text-dark-100 placeholder-gray-500 dark:placeholder-dark-400"
                  :class="{ 'border-red-500': item.searchError }" required />
                <!-- Product Search Results -->
                <div v-if="showProductResults[index] && !item.searchLoading && filteredProducts(index).length > 0"
                  class="absolute z-50 w-full mt-1 bg-white dark:bg-dark-800 border border-gray-300 dark:border-dark-600 rounded-lg shadow-lg max-h-[50vh] sm:max-h-60 overflow-y-auto">
                  <div v-for="product in filteredProducts(index)" :key="product.product_id"
                    @mousedown="() => selectProduct(index, product)"
                    class="px-4 py-2 hover:bg-gray-50 dark:hover:bg-dark-700 cursor-pointer">
                    <div class="font-medium text-gray-900 dark:text-dark-100">
                      {{ product.name }} ({{ product.unit_of_measure }})
                    </div>
                    <div class="text-sm text-gray-500 dark:text-dark-400">
                      ₱{{ formatPrice(product.base_price) }} | Min: {{ product.min_order_quantity || 1 }} {{
                        product.unit_of_measure }}
                    </div>
                  </div>
                </div>
                <!-- Loading State -->
                <div v-if="showProductResults[index] && item.searchLoading"
                  class="absolute z-10 w-full mt-1 bg-white dark:bg-dark-800 border border-gray-300 dark:border-dark-600 rounded-lg shadow-lg p-4 text-center">
                  <LoadingSpinner text="Searching products..." />
                </div>
                <!-- No Results -->
                <div
                  v-if="showProductResults[index] && !item.searchLoading && filteredProducts(index).length === 0 && item.productSearch"
                  class="absolute z-10 w-full mt-1 bg-white dark:bg-dark-800 border border-gray-300 dark:border-dark-600 rounded-lg shadow-lg p-4 text-center text-gray-500 dark:text-dark-400">
                  No products found
                </div>
              </div>
            </div>

            <!-- Quantity, Unit Price, and Dimensions in one row -->
            <div class="grid grid-cols-2 lg:grid-cols-4 gap-2 mb-2">
              <div>
                <label class="block text-xs font-medium text-gray-700 dark:text-dark-300">Quantity *</label>
                <input v-model="item.quantity" type="number" min="0.01" step="0.01" required
                  class="w-full px-2 py-1 text-sm border rounded-lg bg-white dark:bg-dark-700 border-gray-300 dark:border-dark-600 text-gray-900 dark:text-dark-100"
                  @input="validateAndUpdateQuantity(index)" />
              </div>
              <div>
                <label class="block text-xs font-medium text-gray-700 dark:text-dark-300">Unit Price *</label>
                <div class="relative">
                  <span class="absolute left-2 top-1 text-surface-500 dark:text-surface-400 text-sm">₱</span>
                  <input v-model="item.unit_price" type="number" step="0.01" min="0" required readonly
                    class="w-full pl-6 pr-2 py-1 text-sm border rounded-lg bg-surface-50 dark:bg-surface-600 border-surface-300 dark:border-surface-600 text-surface-900 dark:text-surface-100" />
                </div>
              </div>
              <!-- Dimension Fields -->
              <template v-if="item.has_dimensions">
                <div>
                  <label class="flex items-center gap-1.5 text-xs font-medium text-surface-700 dark:text-surface-300">
                    <span>Width ({{ item.dimension_unit }})</span>
                    <span v-if="!item.width" class="text-[10px] font-normal text-surface-500 dark:text-surface-400">(Required)</span>
                    <i v-else class="fas fa-check-circle text-success-600 dark:text-success-400 text-[10px]"></i>
                  </label>
                  <input v-model="item.width" type="number" min="0" step="0.01" required
                    class="w-full px-2 py-1 text-sm border rounded-lg bg-surface-0 dark:bg-surface-700 border-surface-300 dark:border-surface-600 text-surface-900 dark:text-surface-100"
                    @input="updateItemTotal(index)" />
                </div>
                <div>
                  <label class="flex items-center gap-1.5 text-xs font-medium text-surface-700 dark:text-surface-300">
                    <span>Height ({{ item.dimension_unit }})</span>
                    <span v-if="!item.height" class="text-[10px] font-normal text-surface-500 dark:text-surface-400">(Required)</span>
                    <i v-else class="fas fa-check-circle text-success-600 dark:text-success-400 text-[10px]"></i>
                  </label>
                  <input v-model="item.height" type="number" min="0" step="0.01" required
                    class="w-full px-2 py-1 text-sm border rounded-lg bg-surface-0 dark:bg-surface-700 border-surface-300 dark:border-surface-600 text-surface-900 dark:text-surface-100"
                    @input="updateItemTotal(index)" />
                </div>
              </template>
            </div>

            <!-- Item Discount -->
            <div class="mb-2" v-if="!hasTotalDiscount">
              <div class="flex items-center justify-between">
                <label class="text-xs font-medium text-gray-700 dark:text-dark-300">Discount</label>
                <div class="flex items-center gap-2">
                  <label class="inline-flex items-center">
                    <input type="radio" v-model="item.discount_type" value="percentage"
                      class="form-radio text-blue-600 h-3 w-3" @change="updateItemTotal(index)"
                      :disabled="hasTotalDiscount" />
                    <span class="ml-1 text-xs"
                      :class="{ 'text-surface-600 dark:text-surface-400': !hasTotalDiscount, 'text-surface-400 dark:text-surface-500': hasTotalDiscount }">%</span>
                  </label>
                  <label class="inline-flex items-center">
                    <input type="radio" v-model="item.discount_type" value="fixed"
                      class="form-radio text-blue-600 h-3 w-3" @change="updateItemTotal(index)"
                      :disabled="hasTotalDiscount" />
                    <span class="ml-1 text-xs"
                      :class="{ 'text-surface-600 dark:text-surface-400': !hasTotalDiscount, 'text-surface-400 dark:text-surface-500': hasTotalDiscount }">Fixed</span>
                  </label>
                </div>
              </div>
              <div class="relative mt-1">
                <span v-if="item.discount_type === 'fixed'"
                  class="absolute left-2 top-1 text-gray-500 dark:text-dark-400 text-xs">₱</span>
                <span v-if="item.discount_type === 'percentage'"
                  class="absolute right-2 top-1 text-gray-500 dark:text-dark-400 text-xs">%</span>
                <input type="number" v-model.number="item.discount_value" :min="0"
                  :max="item.discount_type === 'percentage' ? 100 : undefined"
                  :step="item.discount_type === 'percentage' ? 1 : 0.01" :disabled="hasTotalDiscount" :class="{
                    'pl-6': item.discount_type === 'fixed',
                    'pr-6': item.discount_type === 'percentage',
                    'bg-surface-100 dark:bg-surface-600': hasTotalDiscount
                  }"
                  class="w-full px-2 py-1 text-sm border rounded-lg bg-surface-0 dark:bg-surface-700 border-surface-300 dark:border-surface-600 text-surface-900 dark:text-surface-100"
                  @input="updateItemTotal(index)" />
              </div>
            </div>

            <!-- Addtl Fees -->
            <div class="mb-2">
              <label class="block text-xs font-medium text-gray-700 dark:text-dark-300 mb-1">
                Addtl Fees
                <span class="text-xs text-gray-500 dark:text-dark-400">(Not visible to customer)</span>
              </label>
              <div class="relative">
                <span class="absolute left-2 top-1 text-surface-500 dark:text-surface-400 text-xs">₱</span>
                <input type="number" v-model.number="item.hidden_fees" min="0" step="0.01" placeholder="0.00"
                  @input="updateItemTotal(index)"
                  class="w-full pl-6 pr-2 py-1 text-sm border rounded-lg focus:ring-2 focus:ring-primary-500 bg-surface-0 dark:bg-surface-700 border-surface-300 dark:border-surface-600 text-surface-900 dark:text-surface-100" />
              </div>
            </div>

            <!-- Item Total -->
            <div class="pt-2 border-t border-gray-200 dark:border-dark-600">
              <div class="space-y-0.5 text-xs">
                <template v-if="item.has_dimensions">
                  <div class="text-gray-900 dark:text-dark-100">Price per unit area: ₱{{ formatPrice(item.unit_price ||
                    0) }}</div>
                  <div class="text-gray-900 dark:text-dark-100">Area: {{ Number(item.width || 0).toFixed(2) }} × {{
                    Number(item.height || 0).toFixed(2) }} {{ item.dimension_unit }}</div>
                  <div class="text-gray-900 dark:text-dark-100">Quantity: {{ Number(item.quantity || 0).toFixed(2) }}
                  </div>
                </template>
                <template v-else>
                  <div class="text-gray-900 dark:text-dark-100">Unit Price: ₱{{ formatPrice(item.unit_price || 0) }}
                  </div>
                  <div class="text-gray-900 dark:text-dark-100">Quantity: {{ Number(item.quantity || 0).toFixed(2) }}
                  </div>
                </template>
                <div class="text-gray-900 dark:text-dark-100">Subtotal: ₱{{ formatPrice(item.subtotal || 0) }}</div>
                <div v-if="item.discount_value > 0" class="text-error-600 dark:text-error-400">
                  <span v-if="item.discount_type === 'percentage'">
                    Discount ({{ item.discount_value }}%): -₱{{ formatPrice(item.discount_amount || 0) }}
                  </span>
                  <span v-else>
                    Discount (₱{{ formatPrice(item.discount_value) }} per unit): -₱{{ formatPrice(item.discount_amount
                      || 0)
                    }}
                  </span>
                </div>
                <div v-if="item.hidden_fees > 0" class="text-blue-600 dark:text-blue-400">
                  Addtl Fees: ₱{{ formatPrice(item.hidden_fees || 0) }}
                </div>
                <div class="font-medium text-green-900 dark:text-green-400">Total: ₱{{ formatPrice(item.total || 0) }}
                </div>
              </div>
            </div>
            </div>
          </div>

          <div v-if="formData.items.length === 0" class="text-center py-8 text-surface-500 dark:text-surface-400">
            No items added yet. Click "Add Item" to start.
          </div>
        </div>

        <!-- Order Total and Discount -->
        <div class="border-t border-surface-200 dark:border-surface-600 p-4 sm:p-6">
          <!-- Total Discount -->
          <div class="mb-4" v-if="formData.items.length > 0">
            <div class="flex items-center justify-between mb-1">
              <label class="block text-sm font-medium text-gray-700 dark:text-dark-300">Total Order Discount</label>
              <div class="flex items-center space-x-2">
                <label class="inline-flex items-center">
                  <input type="radio" v-model="formData.total_discount_type" value="percentage"
                    class="form-radio text-blue-600" :disabled="hasItemDiscounts" @change="handleTotalDiscountChange" />
                  <span class="ml-1 text-sm"
                    :class="{ 'text-gray-600 dark:text-dark-400': !hasItemDiscounts, 'text-gray-400 dark:text-dark-500': hasItemDiscounts }">Percentage
                    (%)</span>
                </label>
                <label class="inline-flex items-center">
                  <input type="radio" v-model="formData.total_discount_type" value="fixed"
                    class="form-radio text-blue-600" :disabled="hasItemDiscounts" @change="handleTotalDiscountChange" />
                  <span class="ml-1 text-sm"
                    :class="{ 'text-surface-600 dark:text-surface-400': !hasItemDiscounts, 'text-surface-400 dark:text-surface-500': hasItemDiscounts }">Fixed
                    Amount</span>
                </label>
              </div>
            </div>
            <div class="relative">
              <span v-if="formData.total_discount_type === 'fixed'"
                class="absolute left-3 top-2 text-gray-500 dark:text-dark-400 text-sm">₱</span>
              <span v-if="formData.total_discount_type === 'percentage'"
                class="absolute right-3 top-2 text-gray-500 dark:text-dark-400 text-sm">%</span>
              <input type="number" v-model.number="formData.total_discount_value" :min="0"
                :max="formData.total_discount_type === 'percentage' ? 100 : undefined"
                :step="formData.total_discount_type === 'percentage' ? 1 : 0.01" :disabled="hasItemDiscounts"
                @input="handleTotalDiscountChange" :class="{
                  'pl-7': formData.total_discount_type === 'fixed',
                  'pr-7': formData.total_discount_type === 'percentage',
                  'bg-surface-100 dark:bg-surface-600': hasItemDiscounts
                }"
                class="w-full px-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-primary-500 disabled:bg-surface-100 dark:disabled:bg-surface-600 disabled:text-surface-500 dark:disabled:text-surface-400 bg-surface-0 dark:bg-surface-700 border-surface-300 dark:border-surface-600 text-surface-900 dark:text-surface-100" />
            </div>
            <div v-if="hasItemDiscounts" class="mt-1 text-sm text-surface-500 dark:text-surface-400 italic">
              Total order discount is disabled when item discounts are applied
            </div>
            <div v-if="formData.total_discount_value > 0 && !hasItemDiscounts"
              class="mt-1 text-sm text-green-600 dark:text-green-400">
              <span v-if="formData.total_discount_type === 'percentage'">
                {{ formData.total_discount_value }}% discount will be applied to the total order (₱{{
                  formatPrice(totalDiscountAmount) }})
              </span>
              <span v-else>
                ₱{{ formatPrice(formData.total_discount_value) }} discount will be applied to the total order
              </span>
            </div>
          </div>

          <!-- Order Totals -->
          <div class="space-y-2">
            <div class="flex justify-between items-center text-lg">
              <span class="font-medium text-surface-900 dark:text-surface-100">Subtotal:</span>
              <span class="text-surface-900 dark:text-surface-100">₱{{ formatPrice(subtotalAmount) }}</span>
            </div>
            <div v-if="totalHiddenFeesAmount > 0"
              class="flex justify-between items-center text-lg text-blue-600 dark:text-blue-400">
              <span class="font-medium">Addtl Fees:</span>
              <span>₱{{ formatPrice(totalHiddenFeesAmount) }}</span>
            </div>
            <div v-if="totalDiscountAmount > 0"
              class="flex justify-between items-center text-lg text-error-600 dark:text-error-400">
              <span class="font-medium">
                <span v-if="hasItemDiscounts">Item Discounts:</span>
                <span v-else>Order Discount:</span>
              </span>
              <span>-₱{{ formatPrice(totalDiscountAmount) }}</span>
            </div>
            <div
              class="flex justify-between items-center text-lg font-bold border-t border-surface-200 dark:border-surface-600 pt-2">
              <span class="text-surface-900 dark:text-surface-100">Total Amount:</span>
              <span class="text-surface-900 dark:text-surface-100">₱{{ formatPrice(finalAmount) }}</span>
            </div>
          </div>
        </div>

        <!-- Update/Submit Button -->
        <div class="p-4 sm:p-6 border-t border-surface-200 dark:border-surface-600">
          <!-- Form validation message -->
          <div v-if="!isFormValid && !loading"
            class="mb-3 p-3 sm:p-4 bg-warning-50 dark:bg-warning-900/20 border border-warning-200 dark:border-warning-600 rounded-lg">
            <div class="flex items-start">
              <i
                class="fas fa-exclamation-triangle text-warning-600 dark:text-warning-400 mr-2 mt-0.5 flex-shrink-0"></i>
              <div class="text-xs sm:text-sm text-warning-800 dark:text-warning-200 flex-1">
                <p class="font-medium mb-1">Please complete the following to create the order:</p>
                <ul class="list-disc list-inside space-y-0.5 sm:space-y-1">
                  <li v-if="!formData.client_id">Select a client</li>
                  <li v-if="!formData.department_id">Select a department</li>
                  <li v-if="!formData.order_type_id">Select an order type</li>
                  <li v-if="!formData.assigned_to">Assign to staff member</li>
                  <li v-if="!formData.gateway_id">Select a gateway</li>
                  <li v-if="!formData.date_received">Set date received</li>
                  <li v-if="!formData.completion_date">Set completion date</li>
                  <li v-if="!formData.deadline">Set deadline</li>
                  <li v-if="!formData.items || formData.items.length === 0">Add at least one order item</li>
                  <li
                    v-if="formData.items && formData.items.length > 0 && formData.items.some(item => !item.product_id || item.quantity <= 0 || (item.has_dimensions && (!item.width || !item.height)))">
                    Complete all order item details</li>
                </ul>
              </div>
            </div>
          </div>

          <div class="flex flex-col sm:flex-row gap-2 sm:justify-end">
            <button v-if="props.embedded" type="button" @click="handleCancel"
              class="w-full sm:w-auto py-2.5 px-4 bg-surface-200 dark:bg-surface-700 hover:bg-surface-300 dark:hover:bg-surface-600 text-surface-700 dark:text-surface-300 font-medium rounded-lg touch-action-manipulation">
              Cancel
            </button>
            <button type="submit" :disabled="loading || !isFormValid || isFormDisabled"
              class="w-full sm:w-auto py-2.5 px-4 bg-primary-600 hover:bg-primary-700 text-white font-medium rounded-lg disabled:opacity-50 disabled:cursor-not-allowed touch-action-manipulation"
              :title="isFormDisabled ? editPermission.reason : ''">
              {{
                isFormDisabled ? 'Editing Restricted' :
                  loading ? 'Processing...' :
                    (isEditing ? 'Update Order' : 'Create Order')
              }}
            </button>
          </div>
        </div>
      </div>
    </form>

    <!-- Error Modal -->
    <Modal v-model="showErrorModal" size="md">
      <template #header>
        <h3 class="text-xl font-semibold">Error</h3>
      </template>
      <template #body>
        <p>{{ errorMessage }}</p>
      </template>
      <template #footer>
        <button @click="() => showErrorModal = false"
          class="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700">
          Close
        </button>
      </template>
    </Modal>

    <!-- Tracking Link Modal -->
    <Modal v-model="showTrackingModal" size="lg" :prevent-outside-close="true">
      <template #title>
        <div class="flex items-center gap-2">
          <i class="fas fa-check-circle text-success-600"></i>
          <span>Order Created Successfully!</span>
        </div>
      </template>
      <template #content>
        <div class="space-y-4 px-4 py-5 sm:p-6">
          <!-- Success Message with Reminder -->
          <div class="bg-info-50 dark:bg-info-900/20 border border-info-200 dark:border-info-600 rounded-lg p-4">
            <div class="flex items-start">
              <i class="fas fa-info-circle text-info-600 dark:text-info-400 mt-0.5 mr-3 text-xl"></i>
              <div>
                <h4 class="font-semibold text-info-900 dark:text-info-100 mb-1">📢 Important Reminder</h4>
                <p class="text-sm text-info-700 dark:text-info-300">
                  Please send this tracking link to the client so they can monitor their order status in real-time.
                  This helps keep clients informed and reduces follow-up inquiries.
                </p>
              </div>
            </div>
          </div>

          <!-- Order Details -->
          <div
            class="bg-surface-50 dark:bg-surface-700 rounded-lg p-4 border border-surface-200 dark:border-surface-600">
            <div class="text-sm">
              <span class="text-surface-600 dark:text-surface-400">Order Number:</span>
              <span class="font-semibold text-surface-900 dark:text-surface-100 ml-2">{{ createdOrderData?.order_number
              }}</span>
              <span class="mx-3 text-surface-400 dark:text-surface-500">|</span>
              <span class="text-surface-600 dark:text-surface-400">Client:</span>
              <span class="font-semibold text-surface-900 dark:text-surface-100 ml-2">{{ clientSearch }}</span>
            </div>
          </div>

          <!-- Tracking Link -->
          <div>
            <label class="block text-sm font-medium text-surface-700 dark:text-surface-300 mb-2">
              📍 Tracking Link
            </label>
            <div class="flex gap-2">
              <input type="text" readonly :value="createdOrderTrackingLink"
                class="flex-1 px-3 py-2 border rounded-lg bg-surface-50 dark:bg-surface-700 text-surface-900 dark:text-surface-100 border-surface-300 dark:border-surface-600 font-mono text-sm" />
              <button @click="copyToClipboard(createdOrderTrackingLink, 'Tracking link')"
                class="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 flex items-center gap-2 transition-colors">
                <i class="fas fa-copy"></i>
                Copy
              </button>
            </div>
          </div>

          <!-- Message Template -->
          <div>
            <label class="block text-sm font-medium text-surface-700 dark:text-surface-300 mb-2">
              💬 Ready-to-Send Message
            </label>
            <div class="flex gap-2">
              <textarea readonly :value="trackingMessage" rows="5"
                class="w-full px-3 py-2 border rounded-lg bg-surface-50 dark:bg-surface-700 text-surface-900 dark:text-surface-100 border-surface-300 dark:border-surface-600 resize-none text-sm"></textarea>
              <button @click="copyToClipboard(trackingMessage, 'Message')"
                class="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 flex items-center gap-2 transition-colors self-start">
                <i class="fas fa-copy"></i>
                Copy
              </button>
            </div>
          </div>

          <!-- Quick Actions -->
          <div
            class="flex items-center justify-between bg-success-50 dark:bg-success-900/20 border border-success-200 dark:border-success-600 rounded-lg p-3">
            <div class="flex items-center gap-2 text-sm text-success-700 dark:text-success-300">
              <i class="fas fa-check-circle"></i>
              <span>Click "Done" when you've sent the link to the client</span>
            </div>
          </div>
        </div>
      </template>
      <template #footer>
        <div class="px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6 gap-3">
          <button @click="closeTrackingModalAndContinue"
            class="w-full sm:w-auto px-6 py-2 bg-success-600 text-white rounded-lg hover:bg-success-700 font-medium transition-colors flex items-center justify-center gap-2">
            <i class="fas fa-check"></i>
            Done - Continue
          </button>
        </div>
      </template>
    </Modal>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useApi } from '@/composables/useApi';
import { useToast } from '@/composables/useToast';
import { useAuth } from '@/composables/useAuth';
import { useMasterData } from '@/composables/useMasterData';
import LoadingSpinner from '@/components/common/LoadingSpinner.vue';
import Modal from '@/components/common/Modal.vue';
import { debounce } from 'lodash-es';
import { clientsApi, ordersApi, usersApi, orderStatusApi } from '@/services/api';
import DepartmentSelector from '@/components/forms/DepartmentSelector.vue';
import OrderTypeSelector from '@/components/forms/OrderTypeSelector.vue';
import { getImageUrl } from '@/utils/imageUtils';
import { useDashboardUtils } from '@/composables/useDashboardUtils';
import { useOrderEditPermissions } from '@/composables/useOrderEditPermissions';

const router = useRouter();
const route = useRoute();
const api = useApi();
const toast = useToast();
const { hasPermission } = useAuth();
const { getCurrentFormattedDate } = useDashboardUtils();
const masterData = useMasterData();
const { loadAssignableUsers, assignableUsers } = masterData;
const { canEditOrder, isSuperAdmin, isEditingRestrictedOrder } = useOrderEditPermissions();

const props = defineProps({
  embedded: {
    type: Boolean,
    default: false
  }
});

const emit = defineEmits(['order-created', 'cancel']);

// State
const loading = ref(false);
const initialLoading = ref(true);
const clients = ref([]);
const products = ref([]);
const showErrorModal = ref(false);
const errorMessage = ref('');
const currentOrder = ref(null);
const editPermission = ref({ allowed: true, reason: null });

// Tracking modal state
const showTrackingModal = ref(false);
const createdOrderData = ref(null);
const formData = ref({
  client_id: '',
  assigned_to: '',
  items: [],
  status: 'pending',
  priority: 'normal',
  deadline: '',
  completion_date: '',
  notes: '',
  department_id: '',
  order_type_id: '',
  payment_due_date: null,
  credit_approved_by: null,
  credit_approved_at: null,
  used_payment_terms: null,
  gateway_id: '',
  image_url: null,
  imageFile: null,
  total_discount_type: 'percentage',
  total_discount_value: 0,
  date_received: getCurrentFormattedDate()
});

// Client search state
const clientSearch = ref('');
const showClientResults = ref(false);
const selectedClient = ref(null);
const clientSearchLoading = ref(false);
const clientSearchError = ref('');

// Product search state
const showProductResults = ref({});
const productSearchTimeout = ref({});

// Collapsible items state
const expandedItems = ref(new Set());

// Computed
const isEditing = computed(() => route.name === 'order-edit');

const totalAmount = computed(() => {
  return formData.value.items.reduce((sum, item) => sum + (item.total || 0), 0);
});

const hasItemDiscounts = computed(() => {
  return formData.value.items.some(item => item.discount_value > 0);
});

const subtotalAmount = computed(() => {
  const sum = formData.value.items.reduce((sum, item) => sum + (item.subtotal || 0), 0);
  return Number(sum.toFixed(2));
});

const totalDiscountAmount = computed(() => {
  // If there are item-level discounts, sum them up
  if (hasItemDiscounts.value) {
    const sum = formData.value.items.reduce((sum, item) => sum + (Number(item.discount_amount) || 0), 0);
    return Number(sum.toFixed(2));
  }

  // If there's a total order discount, calculate it
  if (formData.value.total_discount_value > 0) {
    if (formData.value.total_discount_type === 'percentage') {
      // Calculate percentage of subtotal
      const discountAmount = (subtotalAmount.value * formData.value.total_discount_value) / 100;
      return Number(discountAmount.toFixed(2));
    } else {
      // Fixed amount, but don't exceed subtotal
      return Number(Math.min(formData.value.total_discount_value, subtotalAmount.value).toFixed(2));
    }
  }

  return 0;
});

const finalAmount = computed(() => {
  const totalHiddenFees = formData.value.items.reduce((sum, item) => sum + (Number(item.hidden_fees) || 0), 0);
  return Number((subtotalAmount.value - totalDiscountAmount.value + totalHiddenFees).toFixed(2));
});

const totalHiddenFeesAmount = computed(() => {
  return formData.value.items.reduce((sum, item) => sum + (Number(item.hidden_fees) || 0), 0);
});

const hasTotalDiscount = computed(() => {
  return formData.value.total_discount_value > 0;
});

// Expected tax calculations (internal info only)
const expectedTaxAmount = computed(() => {
  if (!selectedClient.value || !selectedClient.value.withholding_tax_rate || selectedClient.value.withholding_tax_rate <= 0) {
    return 0;
  }
  const orderAmount = finalAmount.value;
  const taxRate = selectedClient.value.withholding_tax_rate / 100;
  // Tax is deducted from payment: net = gross * (1 - tax_rate)
  // So: tax = gross - net = gross - (gross * (1 - tax_rate)) = gross * tax_rate
  // But since client pays net, we calculate: tax = order_amount * tax_rate
  return Number((orderAmount * taxRate).toFixed(2));
});

const expectedNetPayment = computed(() => {
  if (!selectedClient.value || !selectedClient.value.withholding_tax_rate || selectedClient.value.withholding_tax_rate <= 0) {
    return finalAmount.value;
  }
  const orderAmount = finalAmount.value;
  const taxRate = selectedClient.value.withholding_tax_rate / 100;
  // Net payment = order amount - tax
  return Number((orderAmount - expectedTaxAmount.value).toFixed(2));
});

// Tracking modal computed properties
const createdOrderTrackingLink = computed(() => {
  if (!createdOrderData.value) return '';
  return `https://track.igwprints.com/${createdOrderData.value.order_number}/${createdOrderData.value.access_code}`;
});

const trackingMessage = computed(() => {
  if (!createdOrderData.value) return '';
  return `Thank you for doing business with IGW Digital Printing Services!\n\nYour order ${createdOrderData.value.order_number} has been created successfully.\n\nYou can track your order status anytime using this link:\n${createdOrderTrackingLink.value}\n\nIf you have any questions, please don't hesitate to contact us.`;
});

// Methods
const formatPrice = (price) => {
  return Number(price).toLocaleString('en-PH', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
};

// Helper function to format date for input fields (handles timezone correctly)
const formatDateForInput = (dateString) => {
  if (!dateString) return '';

  // Create a date object and format it in local timezone
  const date = new Date(dateString);

  // Get year, month, and day in local timezone
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
};

// Add helper function for classification styling
const getClassificationClass = (classification) => {
  if (!classification) return '';

  const classMap = {
    'vip': 'bg-secondary-100 text-secondary-800',
    'regular': 'bg-primary-100 text-primary-800',
    'new': 'bg-success-100 text-success-800',
    'preferred': 'bg-info-100 text-info-800',
    'inactive': 'bg-surface-100 dark:bg-surface-900/30 text-surface-800 dark:text-surface-300'
  };

  return classMap[classification.toLowerCase()] || 'bg-surface-100 text-surface-800';
};

const addItem = () => {
  // Create a new item with default values
  const newItem = {
    product_id: '',
    productSearch: '',
    quantity: 1.00,
    unit_price: 0,
    subtotal: 0,
    discount_type: 'percentage',
    discount_value: 0,
    discount_amount: 0,
    hidden_fees: 0,
    total: 0,
    has_dimensions: false,
    dimension_unit: null,
    width: null,
    height: null,
    searchLoading: false,
    searchError: '',
    quantityError: ''
  };

  // If there's a total discount applied, ensure item discounts are disabled
  if (hasTotalDiscount.value) {
    newItem.discount_value = 0;
    newItem.discount_amount = 0;
  }

  // Collapse all previous items
  expandedItems.value.clear();
  
  // Add new item
  formData.value.items.push(newItem);
  
  const newIndex = formData.value.items.length - 1;
  
  // Expand the new item
  expandedItems.value.add(newIndex);

  // Initialize the product search results for this item
  showProductResults.value[newIndex] = false;
};

const toggleItem = (index) => {
  if (expandedItems.value.has(index)) {
    expandedItems.value.delete(index);
  } else {
    expandedItems.value.add(index);
  }
};

const removeItem = (index) => {
  formData.value.items.splice(index, 1);
};

const updateItemPrice = async (index) => {
  const item = formData.value.items[index];
  if (!item.product_id) {
    console.log('No product selected, resetting price');

    // Create a new items array with the reset item
    const newItems = [...formData.value.items];
    newItems[index] = {
      ...item,
      unit_price: 0,
      total: 0
    };
    formData.value.items = newItems;
    return;
  }

  console.log('Updating price for product:', item.product_id);
  console.log('Available products:', products.value);

  const product = products.value.find(p => p.product_id === Number(item.product_id));
  if (product) {
    console.log('Found product:', product);
    console.log('Base price:', product.base_price);

    // Create a new items array with the updated item
    const newItems = [...formData.value.items];
    const basePrice = Number(product.base_price);

    console.log('Setting unit price to:', basePrice);

    // Update the item with new values
    newItems[index] = {
      ...item,
      unit_price: basePrice,
      total: Number(item.quantity || 1) * basePrice
    };

    // Replace the entire items array to trigger reactivity
    formData.value.items = newItems;

    console.log('Updated item:', newItems[index]);
  } else {
    console.log('Product not found');

    // Create a new items array with the reset item
    const newItems = [...formData.value.items];
    newItems[index] = {
      ...item,
      unit_price: 0,
      total: 0
    };
    formData.value.items = newItems;
  }
};

const updateItemTotal = (index) => {
  const item = formData.value.items[index];
  if (!item.product_id || !item.quantity) return;

  // Calculate base unit price first
  const unitPrice = Number(item.unit_price) || 0;
  let discountedUnitPrice = unitPrice;
  let discountAmount = 0;

  // Only apply item discount if there's no total discount
  if (item.discount_value > 0 && !hasTotalDiscount.value) {
    if (item.discount_type === 'percentage') {
      discountAmount = (unitPrice * item.discount_value) / 100;
    } else {
      // For fixed discount, don't limit to unit price since it applies per quantity
      discountAmount = item.discount_value;
    }
    discountedUnitPrice = unitPrice - (item.discount_type === 'percentage' ? discountAmount : 0);
  }

  // Calculate subtotal based on dimensions if applicable
  if (item.has_dimensions) {
    // Validate dimension values
    if (!item.width || !item.height) {
      console.warn('Missing dimensions for dimension-based product:', {
        width: item.width,
        height: item.height
      });
      item.subtotal = 0;
      item.discount_amount = 0;
      item.total = 0;
      return;
    }

    // Calculate area first
    const width = Number(item.width) || 0;
    const height = Number(item.height) || 0;
    const area = width * height;
    const quantity = Number(item.quantity) || 0;

    // Calculate original subtotal (without discount)
    item.subtotal = Number((area * unitPrice * quantity).toFixed(2));

    // Calculate discount amount for the entire item
    if (item.discount_type === 'percentage') {
      // For percentage, apply to the entire subtotal
      item.discount_amount = Number(((item.subtotal * item.discount_value) / 100).toFixed(2));
    } else {
      // For fixed, apply the discount per quantity
      item.discount_amount = Number((item.discount_value * quantity).toFixed(2));
    }

    // Calculate final total (include hidden fees)
    const hiddenFees = Number(item.hidden_fees) || 0;
    item.total = Number((item.subtotal - item.discount_amount + hiddenFees).toFixed(2));

    console.log('Dimension calculation details:', {
      width,
      height,
      area,
      unitPrice,
      quantity: item.quantity,
      subtotal: item.subtotal,
      discountAmount: item.discount_amount,
      total: item.total
    });
  } else {
    // For non-dimension products
    const quantity = Number(item.quantity) || 0;
    item.subtotal = Number((unitPrice * quantity).toFixed(2));

    if (item.discount_type === 'percentage') {
      // For percentage, apply to the entire subtotal
      item.discount_amount = Number(((item.subtotal * item.discount_value) / 100).toFixed(2));
    } else {
      // For fixed, apply the discount per quantity
      item.discount_amount = Number((item.discount_value * quantity).toFixed(2));
    }

    // Calculate final total (include hidden fees)
    const hiddenFees = Number(item.hidden_fees) || 0;
    item.total = Number((item.subtotal - item.discount_amount + hiddenFees).toFixed(2));
  }
};

// Update formData when client is selected
const selectClient = (client) => {
  if (!client) return;

  selectedClient.value = client;
  formData.value.client_id = client.id;

  // Use the getClientDisplayName function for consistency
  clientSearch.value = getClientDisplayName(client);

  showClientResults.value = false;

  // Reset order type if it's not allowed for this client
  if (formData.value.order_type_id) {
    const orderType = orderTypes.value.find(t => t.id === formData.value.order_type_id);
    if (orderType) {
      if ((orderType.type === 'pay_later' && !client.pay_later) ||
        (orderType.type === 'purchase_order' && !client.purchase_order)) {
        formData.value.order_type_id = '';
        toast.warning('Selected order type is not allowed for this client');
      }
    }
  }

  // Auto-select department if only one available (only when creating new order)
  if (!isEditing.value && departments.value.length === 1 && !formData.value.department_id) {
    formData.value.department_id = departments.value[0].id;
    console.log('Auto-selected department after client selection:', departments.value[0].name);
  }

  console.log('Selected client:', {
    client_id: formData.value.client_id,
    name: clientSearch.value,
    pay_later: client.pay_later,
    purchase_order: client.purchase_order
  });
};

// Add helper function for client name display
const getClientDisplayName = (client) => {
  if (!client) return '';

  // First try display_name if it exists
  if (client.display_name && client.display_name.trim() !== '') {
    return client.display_name;
  }

  // If company_name exists and either first_name or last_name is empty, prioritize company_name
  if (client.company_name && client.company_name.trim() !== '' &&
    (!client.first_name || !client.last_name || client.first_name.trim() === '' || client.last_name.trim() === '')) {
    return client.company_name;
  }

  // If first_name and last_name both exist, use them
  if (client.first_name && client.last_name &&
    client.first_name.trim() !== '' && client.last_name.trim() !== '') {
    return `${client.first_name} ${client.last_name}`;
  }

  // If only first_name exists
  if (client.first_name && client.first_name.trim() !== '') {
    return client.first_name;
  }

  // If only last_name exists
  if (client.last_name && client.last_name.trim() !== '') {
    return client.last_name;
  }

  // Fallback to company_name again or Unknown Client
  return client.company_name || 'Unknown Client';
};

// Search clients with debounce
const searchClients = async () => {
  try {
    clientSearchLoading.value = true;
    const searchTerm = clientSearch.value.trim();
    console.log('SearchClients - Starting search with term:', searchTerm);

    // Don't search if term is empty or undefined
    if (!searchTerm || searchTerm === 'undefined undefined') {
      clientSearchLoading.value = false;
      return;
    }

    const response = await clientsApi.getAll({
      search: searchTerm,
      limit: 20,
      approval_status: 'approved'
    });

    if (response?.data) {
      const clientsData = Array.isArray(response.data) ? response.data : response.data.data;
      console.log('SearchClients - Results:', clientsData);
      clients.value = clientsData;
    }
  } catch (error) {
    console.error('SearchClients - Error:', error);
    clientSearchError.value = 'Failed to search clients';
  } finally {
    clientSearchLoading.value = false;
  }
};

// Computed property for filtered clients
const filteredClients = computed(() => {
  const searchTerm = clientSearch.value.toLowerCase().trim();
  console.log('FilteredClients - Search term:', searchTerm);

  if (!searchTerm) {
    return clients.value.slice(0, 10);
  }

  return clients.value.filter(client => {
    const companyName = (client.company_name || '').toLowerCase();
    const firstName = (client.first_name || '').toLowerCase();
    const lastName = (client.last_name || '').toLowerCase();
    const fullName = `${firstName} ${lastName}`.toLowerCase();

    // Check if any field contains the search term
    const matches = {
      company: companyName.includes(searchTerm),
      firstName: firstName.includes(searchTerm),
      lastName: lastName.includes(searchTerm),
      fullName: fullName.includes(searchTerm)
    };

    const isMatch = matches.company || matches.firstName || matches.lastName || matches.fullName;

    if (isMatch) {
      console.log('FilteredClients - Match found:', {
        client: client.company_name || `${client.first_name} ${client.last_name}`,
        matches
      });
    }

    return isMatch;
  });
});

// Watch for changes in the search input
watch(clientSearch, (newValue) => {
  if (!newValue || newValue === 'undefined undefined') {
    loadInitialClients();
    return;
  }

  if (newValue.trim().length >= 2) {
    console.log('Search input changed, calling API with:', newValue);
    debouncedSearchClients();
  }
});

const debouncedSearchClients = debounce(() => {
  console.log('DebouncedSearch - Triggered with value:', clientSearch.value);
  searchClients();
}, 300);

// Load initial clients with enhanced logging
const loadInitialClients = async () => {
  try {
    console.log('LoadInitialClients - Starting load...');
    const response = await clientsApi.getAll({
      limit: 20,
      approval_status: 'approved'
    });

    if (response?.data) {
      const initialClients = Array.isArray(response.data) ? response.data : response.data.data;
      console.log('LoadInitialClients - Clients loaded:', initialClients.map(c => ({
        id: c.id,
        company: c.company_name,
        name: `${c.first_name} ${c.last_name}`
      })));
      clients.value = initialClients;
    } else {
      console.log('LoadInitialClients - No data in response');
      throw new Error('No clients data received');
    }
  } catch (error) {
    console.error('LoadInitialClients - Error:', error);
    errorMessage.value = 'Failed to load clients. Please try again.';
    showErrorModal.value = true;
  }
};

// If editing, load the selected client
const loadSelectedClient = async () => {
  if (formData.value.client_id) {
    try {
      const response = await clientsApi.getById(formData.value.client_id);
      if (response?.data) {
        const clientData = response.data;
        selectedClient.value = clientData;
        clientSearch.value = getClientDisplayName(clientData);
        console.log('Loaded selected client:', clientData);
      }
    } catch (error) {
      console.error('Error loading selected client:', error);
      selectedClient.value = null;
      clientSearch.value = '';
    }
  }
};

const loadProducts = async () => {
  try {
    const result = await masterData.loadProducts({ is_active: true });
    console.log('Loaded products from cache:', result);
    products.value = result || [];

    // Log each product for debugging with active status
    products.value.forEach(product => {
      console.log('Product loaded:', {
        id: product.product_id,
        name: product.name,
        base_price: product.base_price,
        is_active: product.is_active
      });
    });

    // Check if any inactive products are loaded
    const inactiveProducts = products.value.filter(p => p.is_active === false);
    if (inactiveProducts.length > 0) {
      console.warn('Found inactive products in order form:', inactiveProducts);
    }
  } catch (error) {
    console.error('Error loading products:', error);
    toast.error('Failed to load products');
  }
};

const imageInput = ref(null);
const imagePreview = ref(null);
const isDragging = ref(false);

const handleImageDrop = (event) => {
  isDragging.value = false;

  try {
    const files = event.dataTransfer.files;

    if (files.length === 0) {
      toast.error('No files were dropped. Please try again.');
      return;
    }

    if (files.length > 1) {
      toast.error('Please drop only one image file at a time.');
      return;
    }

    const file = files[0];
    console.log('Order image dropped:', file.name, file.type, file.size);
    validateAndProcessImage(file);
    toast.success('Image dropped successfully!');
  } catch (error) {
    console.error('Error handling drop event:', error);
    toast.error('Failed to process dropped image. Please try again.');
  }
};

const handleImageUpload = (event) => {
  const file = event.target.files[0];
  if (file) {
    validateAndProcessImage(file);
  }
};

// Handle paste event for clipboard images
const handleImagePaste = async (event) => {
  console.log('Paste event triggered:', event);
  event.preventDefault();
  event.stopPropagation();

  try {
    let imageFound = false;

    // First, try to get image from clipboardData (works for most cases)
    if (event.clipboardData && event.clipboardData.items) {
      console.log('Checking clipboardData items:', event.clipboardData.items.length);

      for (let i = 0; i < event.clipboardData.items.length; i++) {
        const item = event.clipboardData.items[i];
        console.log('Clipboard item:', item.kind, item.type);

        if (item.kind === 'file' && item.type.startsWith('image/')) {
          const file = item.getAsFile();
          if (file) {
            console.log('Order image pasted from clipboardData:', file.name || 'clipboard-image', file.type, file.size);
            validateAndProcessImage(file);
            toast.success('Order image pasted successfully!');
            imageFound = true;
            return;
          }
        }
      }
    }

    // Second, try to get image from files (for drag & drop compatibility)
    if (event.clipboardData && event.clipboardData.files && event.clipboardData.files.length > 0) {
      console.log('Checking clipboardData files:', event.clipboardData.files.length);

      for (let i = 0; i < event.clipboardData.files.length; i++) {
        const file = event.clipboardData.files[i];
        console.log('Clipboard file:', file.name, file.type, file.size);

        if (file.type.startsWith('image/')) {
          console.log('Order image found in files');
          validateAndProcessImage(file);
          toast.success('Order image pasted successfully!');
          imageFound = true;
          return;
        }
      }
    }

    // Third, try modern Clipboard API (for additional browser support)
    if (!imageFound && navigator.clipboard && navigator.clipboard.read) {
      try {
        console.log('Trying modern Clipboard API...');
        const clipboardContents = await navigator.clipboard.read();
        console.log('Clipboard contents:', clipboardContents);

        for (const item of clipboardContents) {
          console.log('Clipboard item types:', item.types);
          for (const type of item.types) {
            if (type.startsWith('image/')) {
              const blob = await item.getType(type);
              const file = new File([blob], 'pasted-order-image', { type: blob.type });
              console.log('Order image from Clipboard API:', file.type, file.size);
              validateAndProcessImage(file);
              toast.success('Order image pasted successfully!');
              imageFound = true;
              return;
            }
          }
        }
      } catch (clipboardError) {
        console.warn('Clipboard API failed:', clipboardError);
      }
    }

    if (!imageFound) {
      console.log('No image found in clipboard');
      toast.error('No image found in clipboard. Try copying an image first (Ctrl+C), then paste it here (Ctrl+V).');
    }
  } catch (error) {
    console.error('Error handling paste event:', error);
    toast.error('Failed to paste image. Please try copying the image again.');
  }
};

const validateAndProcessImage = (file) => {
  // Validate file type and size
  const validTypes = ['image/jpeg', 'image/png', 'image/gif'];
  if (!validTypes.includes(file.type)) {
    toast.error('Please select a valid image file (JPG, PNG, or GIF)');
    return;
  }

  if (file.size > 10 * 1024 * 1024) { // 10MB limit
    toast.error('Image file size must be less than 10MB');
    return;
  }

  // Clear previous validation errors
  validationErrors.value.image_url = '';

  // Clear previous preview if exists
  if (imagePreview.value && imagePreview.value.startsWith('blob:')) {
    URL.revokeObjectURL(imagePreview.value);
  }

  // Set new file and preview
  formData.value.imageFile = file;
  imagePreview.value = URL.createObjectURL(file);
  formData.value.image_url = null; // Clear the old image URL when new image is uploaded

  // Show success message
  toast.success('Order image loaded successfully!');
};

const removeImage = () => {
  // Clear the file input
  if (imageInput.value) {
    imageInput.value.value = '';
  }

  // Clear the preview if it's a blob URL
  if (imagePreview.value && imagePreview.value.startsWith('blob:')) {
    URL.revokeObjectURL(imagePreview.value);
  }

  // Reset all image-related values
  imagePreview.value = null;
  formData.value.imageFile = null;
  formData.value.image_url = null;
};

const loadOrder = async (orderId) => {
  try {
    if (!orderId) {
      throw new Error('Order ID is required');
    }

    loading.value = true;
    console.log('[OrderForm] Loading order:', orderId);

    const response = await api.get(`/orders/${orderId}`);
    console.log('[OrderForm] Order response:', response);

    if (!response?.data) {
      throw new Error('Invalid response data');
    }

    const order = response.data;
    currentOrder.value = order;

    // Check edit permissions for this order
    editPermission.value = canEditOrder(order);

    // Show warning if editing restricted order and user is not super admin
    if (!editPermission.value.allowed && !isSuperAdmin.value) {
      toast.warning(editPermission.value.reason);
    }

    // Reset image preview if exists
    if (imagePreview.value && imagePreview.value.startsWith('blob:')) {
      URL.revokeObjectURL(imagePreview.value);
      imagePreview.value = null;
    }

    // Get product details for each item
    const itemsWithProducts = await Promise.all(order.items?.map(async (item) => {
      try {
        const productResponse = await api.get(`/products/${item.product_id}`);
        const product = productResponse.data;
        return {
          product_id: Number(item.product_id),
          productSearch: product?.name || '',
          quantity: Number(item.quantity),
          unit_price: Number(item.unit_price),
          subtotal: Number(item.subtotal) || 0,
          discount_type: item.discount_type || 'percentage',
          discount_value: Number(item.discount_value) || 0,
          discount_amount: Number(item.discount_amount) || 0,
          hidden_fees: Number(item.hidden_fees) || 0,
          total: Number(item.total),
          specifications: item.specifications || null,
          searchLoading: false,
          searchError: '',
          quantityError: '',
          has_dimensions: product.has_dimensions || false,
          dimension_unit: product.dimension_unit || null,
          width: Number(item.width) || null,
          height: Number(item.height) || null
        };
      } catch (error) {
        console.error('[OrderForm] Error loading product:', error);
        return null;
      }
    }) || []);

    // Filter out any failed product loads
    const validItems = itemsWithProducts.filter(item => item !== null);

    formData.value = {
      client_id: Number(order.client_id),
      assigned_to: order.assigned_to ? Number(order.assigned_to) : '',
      items: validItems,
      status: order.status || 'not_started',
      priority: order.priority || 'normal',
      deadline: order.deadline ? formatDateForInput(order.deadline) : '',
      completion_date: order.completion_date ? formatDateForInput(order.completion_date) : '',
      notes: order.notes || '',
      department_id: order.department_id ? Number(order.department_id) : '',
      order_type_id: order.order_type_id ? Number(order.order_type_id) : '',
      gateway_id: order.gateway_id ? Number(order.gateway_id) : '',
      image_url: order.image_url || null,
      imageFile: null,
      total_discount_type: order.total_discount_type || 'percentage',
      total_discount_value: Number(order.total_discount_value) || 0,
      date_received: order.date_received ? formatDateForInput(order.date_received) : getCurrentFormattedDate()
    };

    // Expand all items when editing
    expandedItems.value.clear();
    validItems.forEach((_, index) => {
      expandedItems.value.add(index);
    });

    // Set image preview from order's image_url
    if (order.image_url) {
      imagePreview.value = null; // Clear any existing preview
      formData.value.image_url = getImageUrl(order.image_url);
      console.log('[OrderForm] Setting image URL:', formData.value.image_url);
    }

    await loadSelectedClient();

    // Recalculate all item totals to ensure consistency
    formData.value.items.forEach((item, index) => {
      updateItemTotal(index);
    });

    console.log('[OrderForm] Order loaded successfully:', formData.value);
  } catch (error) {
    console.error('[OrderForm] Error loading order:', error);
    const errorMessage = error.response?.data?.error || error.message || 'Failed to load order details';
    toast.error(errorMessage);
    router.push('/orders');
  } finally {
    loading.value = false;
  }
};

const validationErrors = ref({
  department_id: '',
  order_type_id: '',
  client_id: '',
  items: '',
  assigned_to: '',
  gateway_id: '',
  image_url: '',
  date_received: '',
  completion_date: '',
  deadline: '',
  // ... other validation errors
});

const handleOrderTypeValidation = (validation) => {
  if (validation.requiresApproval) {
    // Handle approval requirement
    formData.value.requires_approval = true;
  }

  if (validation.paymentTerms) {
    formData.value.used_payment_terms = validation.paymentTerms;
    // Calculate payment due date based on terms
    const dueDate = new Date();
    dueDate.setDate(dueDate.getDate() + validation.paymentTerms);
    formData.value.payment_due_date = dueDate.toISOString().split('T')[0];
  }
};

// Add users state and loading function
const users = ref([]);

const loadUsers = async () => {
  try {
    console.log('[OrderForm] Loading users from cache');
    const assignableUsersData = await loadAssignableUsers();
    // Filter for active users who can be assigned to orders
    users.value = assignableUsersData.filter(user => user.is_active !== false);
    console.log('[OrderForm] Loaded', users.value.length, 'assignable users from cache');
  } catch (error) {
    console.error('[OrderForm] Error loading assignable users:', error);
    errorMessage.value = 'Failed to load staff members. Please try again.';
    showErrorModal.value = true;
  }
};

// Add new state for order statuses
const orderStatuses = ref([]);

// Add function to load order statuses from cache
const loadOrderStatuses = async () => {
  try {
    const result = await masterData.loadOrderStatuses();
    orderStatuses.value = result || [];
  } catch (error) {
    console.error('Error loading order statuses:', error);
    toast.error('Failed to load order statuses');
  }
};

// Add gateways state
const gateways = ref([]);

// Add function to load gateways
const loadGateways = async () => {
  try {
    console.log('Loading gateways...');
    const response = await api.get('/clients/gateways');
    console.log('Gateway response:', response);

    if (response?.data) {
      gateways.value = Array.isArray(response.data) ? response.data : [];
      console.log('Loaded gateways:', gateways.value);
    } else {
      console.warn('No gateway data in response');
      gateways.value = [];
    }
  } catch (error) {
    console.error('Error loading gateways:', {
      message: error.message,
      response: error.response?.data,
      status: error.response?.status
    });
    // Instead of showing error modal, show a toast notification
    toast.error('Failed to load gateways. Please refresh the page or contact support.');
    gateways.value = [];
  }
};

// Add orderTypes state
const orderTypes = ref([]);

// Add departments state
const departments = ref([]);

// Add function to load departments
const loadDepartments = async () => {
  try {
    const result = await masterData.loadDepartments();
    departments.value = result || [];
  } catch (error) {
    console.error('Error loading departments:', error);
    toast.error('Failed to load departments');
  }
};

// Add function to load order types
const loadOrderTypes = async () => {
  try {
    console.log('Loading order types...');
    const response = await api.get('/order-types');
    if (response?.data) {
      orderTypes.value = Array.isArray(response.data) ? response.data : response.data.data;
      console.log('Loaded order types:', orderTypes.value);
    }
  } catch (error) {
    console.error('Error loading order types:', error);
    toast.error('Failed to load order types');
  }
};

// Initialize component
const initializeForm = async () => {
  try {
    initialLoading.value = true;

    await Promise.all([
      loadInitialClients(),
      loadProducts(),
      loadUsers(),
      loadOrderStatuses(),
      loadGateways(),
      loadDepartments(),
      loadOrderTypes()
    ]);

    if (isEditing.value && route.params.id) {
      console.log('Loading existing order:', route.params.id);
      await loadOrder(route.params.id);
    } else {
      console.log('Creating new order');
      formData.value = {
        client_id: '',
        assigned_to: '',
        items: [],
        status: 'pending',
        priority: 'normal',
        deadline: '',
        completion_date: '',
        notes: '',
        department_id: '',
        order_type_id: '',
        payment_due_date: null,
        credit_approved_by: null,
        credit_approved_at: null,
        used_payment_terms: null,
        gateway_id: '',
        image_url: null,
        imageFile: null,
        total_discount_type: 'percentage',
        total_discount_value: 0,
        date_received: getCurrentFormattedDate()
      };
      // Clear expanded items when creating new order
      expandedItems.value.clear();
    }
  } catch (error) {
    console.error('Error initializing form:', error);
    toast.error('Failed to initialize form. Please try again.');
    router.push('/orders');
  } finally {
    initialLoading.value = false;
  }
};

// Methods
const handleClientSearchBlur = (e) => {
  // Only hide if click is outside the client-search container
  const relatedTarget = e.relatedTarget;
  if (!relatedTarget || !relatedTarget.closest('.client-search')) {
    setTimeout(() => {
      showClientResults.value = false;
    }, 200);
  }
};

const handleSubmit = async () => {
  try {
    loading.value = true;
    console.log('Submitting form with data:', formData.value);

    // Reset validation errors
    validationErrors.value = {};

    // Validate required fields
    let hasValidationErrors = false;

    if (!formData.value.date_received) {
      validationErrors.value.date_received = 'Date Received is required';
      hasValidationErrors = true;
    }

    if (!formData.value.completion_date) {
      validationErrors.value.completion_date = 'Completion Date is required';
      hasValidationErrors = true;
    }

    if (!formData.value.deadline) {
      validationErrors.value.deadline = 'Deadline is required';
      hasValidationErrors = true;
    }

    // Validate order items
    if (!formData.value.items || formData.value.items.length === 0) {
      validationErrors.value.items = 'At least one order item is required';
      hasValidationErrors = true;
    } else {
      // Validate each item
      const invalidItems = formData.value.items.some(item => {
        if (!item.product_id || item.product_id === '') return true;
        if (!item.quantity || item.quantity <= 0) return true;

        // For dimension-based products, validate dimensions
        if (item.has_dimensions) {
          if (!item.width || item.width <= 0 || !item.height || item.height <= 0) {
            return true;
          }
        }

        return false;
      });

      if (invalidItems) {
        validationErrors.value.items = 'All order items must have a product selected, valid quantity, and dimensions (if applicable)';
        hasValidationErrors = true;
      }
    }

    if (hasValidationErrors) {
      toast.error('Please fill in all required fields and add at least one valid order item');
      loading.value = false;
      return;
    }

    // Create FormData for file upload
    const submitData = new FormData();

    // Format items data before submission
    const formattedItems = formData.value.items.map(item => ({
      product_id: Number(item.product_id),
      quantity: Number(item.quantity),
      unit_price: Number(item.unit_price),
      subtotal: Number(item.subtotal),
      discount_type: item.discount_type,
      discount_value: Number(item.discount_value),
      discount_amount: Number(item.discount_amount),
      hidden_fees: Number(item.hidden_fees) || 0,
      total: Number(item.total),
      has_dimensions: Boolean(item.has_dimensions),
      dimension_unit: item.dimension_unit,
      width: item.has_dimensions ? Number(item.width) : null,
      height: item.has_dimensions ? Number(item.height) : null,
      specifications: item.specifications || null
    }));

    console.log('Formatted items for submission:', formattedItems);

    // Add all form fields to FormData
    Object.keys(formData.value).forEach(key => {
      if (key === 'items') {
        // Handle items array with formatted data
        submitData.append('items', JSON.stringify(formattedItems));
      } else if (key === 'imageFile' && formData.value.imageFile) {
        // Handle image file
        submitData.append('image', formData.value.imageFile);
      } else if (formData.value[key] !== null && formData.value[key] !== undefined) {
        // Handle all other fields
        submitData.append(key, formData.value[key]);
      }
    });

    // Log FormData contents for debugging
    for (let [key, value] of submitData.entries()) {
      console.log('FormData entry:', key, value);
    }

    let response;
    if (isEditing.value) {
      console.log('Updating order:', route.params.id);
      response = await api.put(`/orders/${route.params.id}`, submitData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
    } else {
      console.log('Creating new order');
      response = await api.post('/orders', submitData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
    }

    console.log('API Response:', response);

    if (response?.data) {
      // Use the message from the backend response, or fallback to default messages
      const successMessage = response.data.message ||
        (isEditing.value ? 'Order updated successfully' : 'Order created successfully');

      toast.success(successMessage);

      // For new orders, show tracking modal
      if (!isEditing.value && response.data.data) {
        createdOrderData.value = response.data.data;
        showTrackingModal.value = true;
        // Don't navigate or emit yet - wait for modal to be closed
      } else if (isEditing.value) {
        // For edits, navigate/emit immediately
        if (props.embedded) {
          emit('order-created', response.data);
        } else {
          router.push('/orders');
        }
      } else {
        // Fallback: if no order data, navigate/emit immediately
        if (props.embedded) {
          emit('order-created', response.data);
        } else {
          router.push('/orders');
        }
      }
    }
  } catch (error) {
    console.error('Form submission error:', error);

    if (error.response?.data?.error) {
      toast.error(error.response.data.error);

      // Handle validation errors if they exist
      if (error.response.data.validationErrors) {
        validationErrors.value = error.response.data.validationErrors;
      }
    } else {
      toast.error('Failed to save order. Please try again.');
    }
  } finally {
    loading.value = false;
  }
};

// Add a cancel method for embedded mode
const handleCancel = () => {
  if (props.embedded) {
    emit('cancel');
  } else {
    router.back();
  }
};

// Tracking modal methods
const copyToClipboard = async (text, label = 'Text') => {
  try {
    await navigator.clipboard.writeText(text);
    toast.success(`${label} copied to clipboard!`);
  } catch (err) {
    console.error('Failed to copy text:', err);
    toast.error('Failed to copy to clipboard');
  }
};

const closeTrackingModalAndContinue = () => {
  showTrackingModal.value = false;

  if (props.embedded) {
    // Emit event when embedded in a modal
    emit('order-created', createdOrderData.value);
  } else {
    // Navigate to orders page when standalone
    router.push('/orders');
  }
};

// Lifecycle
onMounted(() => {
  initializeForm();
});

// Add these new methods before the handleSubmit method:
const debouncedSearchProducts = debounce((index) => {
  const item = formData.value.items[index];
  if (!item) return;

  if (!item.productSearch || item.productSearch.trim().length < 2) {
    return;
  }

  searchProducts(index);
}, 300);

const searchProducts = async (index) => {
  const item = formData.value.items[index];
  if (!item) return;

  try {
    item.searchLoading = true;
    const searchTerm = item.productSearch.trim();

    const response = await api.get('/products', {
      params: {
        search: searchTerm,
        is_active: true,
        limit: 20
      }
    });

    if (response?.data) {
      const searchResults = Array.isArray(response.data) ? response.data : response.data.data;

      // Double-check that only active products are included
      const activeProducts = searchResults.filter(product => product.is_active !== false);
      products.value = activeProducts;

      console.log(`Search results for "${searchTerm}":`, {
        total: searchResults.length,
        active: activeProducts.length,
        filtered: searchResults.length - activeProducts.length
      });

      // Log any inactive products that might have slipped through
      const inactiveInSearch = searchResults.filter(p => p.is_active === false);
      if (inactiveInSearch.length > 0) {
        console.warn('Found inactive products in search results:', inactiveInSearch);
      }
    }
  } catch (error) {
    console.error('Error searching products:', error);
    item.searchError = 'Failed to search products';
  } finally {
    item.searchLoading = false;
  }
};

const filteredProducts = (index) => {
  const item = formData.value.items[index];
  if (!item || !item.productSearch) {
    // Only show active products even when no search term
    return products.value.filter(product => product.is_active !== false).slice(0, 10);
  }

  const searchTerm = item.productSearch.toLowerCase().trim();
  return products.value.filter(product =>
    // Only include active products
    product.is_active !== false &&
    (product.name.toLowerCase().includes(searchTerm) ||
      product.unit_of_measure.toLowerCase().includes(searchTerm))
  );
};

const handleProductSearchBlur = (index) => {
  setTimeout(() => {
    showProductResults.value[index] = false;
  }, 200);
};

const selectProduct = (index, product) => {
  const item = formData.value.items[index];
  if (!item) return;

  console.log('Selecting product:', {
    product_id: product.product_id,
    name: product.name,
    base_price: product.base_price,
    has_dimensions: product.has_dimensions,
    dimension_unit: product.dimension_unit
  });

  // Reset item values
  item.product_id = product.product_id;
  item.productSearch = product.name;
  item.unit_price = Number(product.base_price);
  item.quantity = Number(Number(product.min_order_quantity || 1).toFixed(2));

  // Handle dimensions
  if (product.has_dimensions) {
    item.has_dimensions = true;
    item.dimension_unit = product.dimension_unit;
    // Initialize width and height to 0 instead of null
    item.width = 0;
    item.height = 0;

    console.log('Initialized dimension-based product:', {
      product_id: item.product_id,
      has_dimensions: item.has_dimensions,
      dimension_unit: item.dimension_unit,
      width: item.width,
      height: item.height
    });
  } else {
    // Reset dimension fields for non-dimension products
    item.has_dimensions = false;
    item.dimension_unit = null;
    item.width = null;
    item.height = null;

    console.log('Initialized non-dimension product:', {
      product_id: item.product_id,
      has_dimensions: item.has_dimensions
    });
  }

  // Calculate initial total
  updateItemTotal(index);
  showProductResults.value[index] = false;
};

const handleTotalDiscountChange = () => {
  // If total discount is applied, clear all item discounts
  if (formData.value.total_discount_value > 0) {
    // Create a copy of the items array to ensure reactivity
    const updatedItems = formData.value.items.map(item => ({
      ...item,
      discount_value: 0,
      discount_amount: 0
    }));

    // Replace the items array
    formData.value.items = updatedItems;

    // Update totals for all items
    formData.value.items.forEach((_, idx) => {
      updateItemTotal(idx);
    });

    console.log('Cleared all item discounts due to total discount application');
  }
};

const getMinQuantity = (productId) => {
  if (!productId) return 1;
  const product = products.value.find(p => p.product_id === Number(productId));
  return product?.min_order_quantity || 1;
};

const validateAndUpdateQuantity = (index) => {
  const item = formData.value.items[index];
  if (!item || !item.product_id) return;

  // Convert to number and fix to 2 decimal places
  item.quantity = Number(Number(item.quantity).toFixed(2));

  const minQuantity = getMinQuantity(item.product_id);
  if (item.quantity < 0.01) {
    item.quantityError = 'Quantity must be at least 0.01';
  } else if (item.quantity < minQuantity) {
    item.quantityError = `Recommended minimum order quantity is ${minQuantity}`;
  } else {
    item.quantityError = '';
  }

  updateItemTotal(index);
};

// Add computed property for filtered order types
const filteredOrderTypes = computed(() => {
  if (!selectedClient.value) return [];

  console.log('Original order types:', orderTypes.value);
  console.log('Selected client payment options:', {
    client_id: selectedClient.value.id,
    pay_later: selectedClient.value.pay_later,
    purchase_order: selectedClient.value.purchase_order
  });

  const filtered = orderTypes.value.filter(type => {
    // Log each type being evaluated
    console.log('Evaluating order type:', {
      id: type.id,
      name: type.name,
      type: type.type,
      code: type.code
    });

    // Check "Pay Later" type by name
    if (type.name && type.name.toLowerCase().includes('pay later')) {
      if (!selectedClient.value.pay_later) {
        console.log(`Filtering out Pay Later type: ${type.name}`);
        return false;
      }
    }

    // Check "Purchase Order" type by name
    if (type.name && type.name.toLowerCase().includes('purchase order')) {
      if (!selectedClient.value.purchase_order) {
        console.log(`Filtering out Purchase Order type: ${type.name}`);
        return false;
      }
    }

    // Also check by type property if available
    if (type.type === 'pay_later' && !selectedClient.value.pay_later) {
      console.log(`Filtering out pay_later type: ${type.name}`);
      return false;
    }
    if (type.type === 'purchase_order' && !selectedClient.value.purchase_order) {
      console.log(`Filtering out purchase_order type: ${type.name}`);
      return false;
    }

    return true;
  });

  console.log('Filtered order types:', filtered);
  return filtered;
});

// Watch filtered order types to auto-select if only one option
watch(filteredOrderTypes, (filtered) => {
  // Only auto-select if not editing and order_type_id is not already set
  if (!isEditing.value && filtered.length === 1 && !formData.value.order_type_id) {
    formData.value.order_type_id = filtered[0].id;
    console.log('Auto-selected filtered order type:', filtered[0].name);
  }
}, { immediate: false });

// Computed property to check if form should be disabled
const isFormDisabled = computed(() => {
  return isEditing.value && !editPermission.value.allowed && !isSuperAdmin.value;
});

// Computed property to check if all required fields are filled
const isFormValid = computed(() => {
  // If form is disabled due to permissions, it's not valid for submission
  if (isFormDisabled.value) {
    return false;
  }

  // Check all required fields
  const requiredFields = [
    formData.value.client_id,
    formData.value.department_id,
    formData.value.order_type_id,
    formData.value.assigned_to,
    formData.value.gateway_id,
    formData.value.date_received,
    formData.value.completion_date,
    formData.value.deadline
  ];

  // Check if all required fields are filled
  const allFieldsFilled = requiredFields.every(field => field !== '' && field !== null && field !== undefined);

  // Check if at least one order item exists
  const hasOrderItems = formData.value.items && formData.value.items.length > 0;

  // Check if all order items are valid (have product selected and valid quantities)
  const allItemsValid = hasOrderItems && formData.value.items.every(item => {
    const hasProduct = item.product_id && item.product_id !== '';
    const hasValidQuantity = item.quantity && item.quantity > 0;

    // For dimension-based products, check if width and height are provided
    if (item.has_dimensions) {
      const hasValidDimensions = item.width && item.width > 0 && item.height && item.height > 0;
      return hasProduct && hasValidQuantity && hasValidDimensions;
    }

    return hasProduct && hasValidQuantity;
  });

  return allFieldsFilled && hasOrderItems && allItemsValid;
});
</script>

<style scoped>
/* Mobile-specific improvements for Order Form */
@media (max-width: 1023px) {
  .order-form {
    padding: 0;
  }

  /* Ensure form fields are readable on mobile */
  input[type="text"],
  input[type="number"],
  input[type="date"],
  select,
  textarea {
    font-size: 16px;
    /* Prevents zoom on iOS */
  }

  /* Better touch targets for mobile */
  button,
  .nav-link {
    min-height: 44px;
    min-width: 44px;
  }

  /* Improve dropdown visibility on mobile */
  .client-search .absolute,
  .relative .absolute {
    z-index: 50;
    max-height: 50vh;
  }

  /* Better spacing for order items on mobile */
  .order-form .space-y-4>*+* {
    margin-top: 1rem;
  }

  /* Ensure order items section is scrollable */
  .order-form .overflow-y-auto {
    -webkit-overflow-scrolling: touch;
    scrollbar-width: thin;
  }

  /* Improve image upload area on mobile */
  .order-form .border-2 {
    padding: 1rem;
    min-height: 120px;
  }

  /* Better button spacing on mobile */
  .order-form .flex.gap-2 {
    gap: 0.75rem;
  }
}

/* Tablet and up - maintain desktop layout */
@media (min-width: 1024px) {
  .order-form {
    max-width: 100%;
  }
}
</style>